/**
 * Audio Utility for Workout Timers
 *
 * Provides sound effects and audio cues for timers:
 * - Beeps for countdowns
 * - Voice announcements (using Web Speech API)
 * - Completion sounds
 */

class AudioManager {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.speechSynthesis = window.speechSynthesis;
  }

  /**
   * Initialize audio context (call on user interaction)
   */
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
  }

  /**
   * Play a beep sound
   * @param {number} frequency - Frequency in Hz (default: 800)
   * @param {number} duration - Duration in ms (default: 100)
   * @param {number} volume - Volume 0-1 (default: 0.3)
   */
  beep(frequency = 800, duration = 100, volume = 0.3) {
    if (!this.enabled || !this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration / 1000);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + duration / 1000);
  }

  /**
   * Play countdown beeps (3-2-1)
   */
  countdownBeep(secondsRemaining) {
    if (secondsRemaining === 3) {
      this.beep(600, 150, 0.2); // Low beep
    } else if (secondsRemaining === 2) {
      this.beep(700, 150, 0.25); // Medium beep
    } else if (secondsRemaining === 1) {
      this.beep(800, 150, 0.3); // Higher beep
    } else if (secondsRemaining === 0) {
      this.beep(1000, 300, 0.4); // Highest, longest beep
    }
  }

  /**
   * Play interval transition beep (EMOM/Tabata)
   */
  transitionBeep() {
    this.beep(1200, 200, 0.35);
  }

  /**
   * Play workout complete sound (success!)
   */
  completionSound() {
    if (!this.enabled || !this.audioContext) return;

    // Play a pleasant ascending tone sequence
    setTimeout(() => this.beep(523, 150, 0.3), 0);    // C
    setTimeout(() => this.beep(659, 150, 0.3), 150);  // E
    setTimeout(() => this.beep(784, 300, 0.35), 300); // G
  }

  /**
   * Speak text using Web Speech API
   * @param {string} text - Text to speak
   * @param {number} rate - Speech rate 0.1-10 (default: 1)
   */
  speak(text, rate = 1.2) {
    if (!this.enabled || !this.speechSynthesis) return;

    // Cancel any ongoing speech
    this.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = rate;
    utterance.pitch = 1;
    utterance.volume = 0.8;

    this.speechSynthesis.speak(utterance);
  }

  /**
   * Announce next exercise
   * @param {string} exerciseName - Name of the exercise
   */
  announceExercise(exerciseName) {
    this.speak(`Next: ${exerciseName}`);
  }

  /**
   * Announce time remaining
   * @param {number} seconds - Seconds remaining
   */
  announceTime(seconds) {
    if (seconds === 60) {
      this.speak('One minute remaining');
    } else if (seconds === 30) {
      this.speak('Thirty seconds');
    } else if (seconds === 10) {
      this.speak('Ten seconds');
    }
  }

  /**
   * Toggle audio on/off
   */
  toggle() {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  /**
   * Enable audio
   */
  enable() {
    this.enabled = true;
  }

  /**
   * Disable audio
   */
  disable() {
    this.enabled = false;
  }

  /**
   * Check if audio is enabled
   */
  isEnabled() {
    return this.enabled;
  }
}

// Export singleton instance
export const audioManager = new AudioManager();
export default audioManager;
